import React from 'react';
    import { motion } from 'framer-motion';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiFileText, FiShield, FiUser, FiAlertCircle, FiCheck, FiX, FiGift } = FiIcons;

    const Terms = () => {
      return (
        <section className="pt-32 pb-20 px-6 min-h-screen bg-gradient-to-br from-rose-light via-white to-rose-accent/20">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <div className="flex items-center justify-center space-x-2 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-rose-primary to-rose-secondary rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiFileText} className="text-white text-xl" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold">
                  <span className="bg-gradient-to-r from-rose-primary to-rose-secondary bg-clip-text text-transparent">
                    Terms of Service
                  </span>
                </h1>
              </div>
              <p className="text-gray-600 text-lg">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
            >
              <div className="prose prose-lg max-w-none">
                <div className="space-y-8">
                  {/* Introduction */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                      <SafeIcon icon={FiUser} className="text-rose-primary mr-3" />
                      1. Agreement to Terms
                    </h2>
                    <p className="text-gray-600 leading-relaxed">
                      Welcome to Gifter's Choice. By downloading, accessing, or using our mobile application and services, 
                      you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, 
                      please do not use our service.
                    </p>
                  </div>

                  {/* Description of Service */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                      <SafeIcon icon={FiGift} className="text-rose-primary mr-3" />
                      2. Description of Service
                    </h2>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Gifter's Choice is a mobile application that allows users to:
                    </p>
                    <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                      <li>Create and manage gift wish lists</li>
                      <li>Import products from various online stores</li>
                      <li>Categorize items as "wants" or "needs"</li>
                      <li>Share lists with friends and family</li>
                      <li>Participate in gift exchange groups</li>
                    </ul>
                  </div>

                  {/* User Responsibilities */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                      <SafeIcon icon={FiShield} className="text-rose-primary mr-3" />
                      3. User Responsibilities
                    </h2>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      As a user of Gifter's Choice, you agree to:
                    </p>
                    <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                      <li>Provide accurate and complete information when creating your account</li>
                      <li>Use the service for lawful purposes only</li>
                      <li>Respect the intellectual property rights of others</li>
                      <li>Not attempt to gain unauthorized access to our systems</li>
                      <li>Not use the service to harass, abuse, or harm others</li>
                      <li>Maintain the security of your account credentials</li>
                    </ul>
                  </div>

                  {/* Content and Intellectual Property */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                      <SafeIcon icon={FiFileText} className="text-rose-primary mr-3" />
                      4. Content and Intellectual Property
                    </h2>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      <strong>Your Content:</strong> You retain ownership of any content you add to your wish lists, 
                      including product descriptions, images, and personal preferences. By using our service, you grant 
                      us a limited, non-exclusive license to use, store, and display your content solely for the purpose 
                      of providing the service.
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      <strong>Our Content:</strong> The Gifter's Choice application, including its design, features, 
                      and proprietary technology, is protected by copyright, trademark, and other intellectual 
                      property laws. You may not copy, modify, or distribute our proprietary content without our 
                      express written permission.
                    </p>
                  </div>

                  {/* Privacy */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                      <SafeIcon icon={FiShield} className="text-rose-primary mr-3" />
                      5. Privacy
                    </h2>
                    <p className="text-gray-600 leading-relaxed">
                      Your privacy is important to us. Please review our Privacy Policy, which explains how we collect, 
                      use, and protect your information. By using our service, you consent to the collection and use 
                      of your information as described in our Privacy Policy.
                    </p>
                  </div>

                  {/* Limitation of Liability */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                      <SafeIcon icon={FiAlertCircle} className="text-rose-primary mr-3" />
                      6. Limitation of Liability
                    </h2>
                    <p className="text-gray-600 leading-relaxed">
                      To the maximum extent permitted by law, Gifter's Choice shall not be liable for any indirect, 
                      incidental, special, or consequential damages arising out of or in connection with your use 
                      of the service. Our total liability to you for all claims shall not exceed the amount you paid 
                      for the service, if any.
                    </p>
                  </div>

                  {/* Termination */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                      <SafeIcon icon={FiX} className="text-rose-primary mr-3" />
                      7. Termination
                    </h2>
                    <p className="text-gray-600 leading-relaxed">
                      We may terminate or suspend your account at any time, with or without cause, with or without notice. 
                      You may also terminate your account at any time by deleting the application or contacting us. 
                      Upon termination, your right to use the service will cease immediately.
                    </p>
                  </div>

                  {/* Changes to Terms */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                      <SafeIcon icon={FiAlertCircle} className="text-rose-primary mr-3" />
                      8. Changes to Terms
                    </h2>
                    <p className="text-gray-600 leading-relaxed">
                      We reserve the right to modify these Terms at any time. We will notify you of any changes by 
                      posting the updated Terms in the app and updating the "Last updated" date. Your continued use 
                      of the service after such modifications constitutes acceptance of the updated Terms.
                    </p>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                      <SafeIcon icon={FiUser} className="text-rose-primary mr-3" />
                      9. Contact Information
                    </h2>
                    <p className="text-gray-600 leading-relaxed">
                      If you have any questions about these Terms of Service, please contact us at:
                    </p>
                    <div className="mt-4 p-4 bg-rose-light rounded-lg">
                      <p className="text-gray-700">
                        <strong>Email:</strong> hello@gifterschoice.app<br />
                        <strong>Subject:</strong> Terms of Service Inquiry
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      );
    };

    export default Terms;